/**
 * Gitignore + built-in denylist for the file agent.
 * The matcher implements a pragmatic subset of gitignore semantics — enough
 * to hide the noisy folders agents shouldn't touch (node_modules, build
 * outputs, env files) without pulling in a full gitignore parser.
 *
 * @module file-agent/lib/gitignore
 */

/** Always-hidden paths regardless of the project's `.gitignore`. */
const DEFAULT_DENYLIST: ReadonlyArray<string> = [
  "node_modules",
  ".git",
  ".turbo",
  ".next",
  ".vite",
  ".cache",
  "dist",
  "build",
  "out",
  ".venv",
  "__pycache__",
  ".DS_Store",
  ".env",
  ".env.local",
  ".env.development",
  ".env.production",
];

/** A compiled gitignore-style pattern. `negate` flips a parent pattern. */
interface Pattern {
  regex: RegExp;
  negate: boolean;
  /** True when the pattern is anchored (begins with /) — matches from root only. */
  anchored: boolean;
  /** True when the pattern targets directories only (trailing /). */
  dirOnly: boolean;
}

/**
 * Compile gitignore patterns from a `.gitignore` file's text contents.
 * Lines starting with `#` and blank lines are skipped. `!pattern` lines
 * are negations. Globs supported: `*`, `?`, `**`. Anchoring (`/foo`) and
 * trailing slashes (`build/`) are honoured; nothing fancier.
 */
export function compileGitignore(source: string): Pattern[] {
  const patterns: Pattern[] = [];
  const lines = source.split(/\r?\n/);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line || line.startsWith("#")) continue;
    let body = line;
    let negate = false;
    if (body.startsWith("!")) {
      negate = true;
      body = body.slice(1);
    }
    const dirOnly = body.endsWith("/");
    if (dirOnly) body = body.slice(0, -1);
    const anchored = body.startsWith("/");
    if (anchored) body = body.slice(1);

    patterns.push({
      regex: globToRegex(body, anchored),
      negate,
      anchored,
      dirOnly,
    });
  }
  return patterns;
}

/**
 * Convert a gitignore-style glob into a regex matching paths relative to
 * the repo root. Supports `**`, `*`, `?`. Other regex metacharacters are
 * escaped.
 */
function globToRegex(pattern: string, anchored: boolean): RegExp {
  let regex = "";
  for (let i = 0; i < pattern.length; i++) {
    const ch = pattern[i];
    if (ch === "*") {
      if (pattern[i + 1] === "*") {
        regex += ".*";
        i++;
      } else {
        regex += "[^/]*";
      }
    } else if (ch === "?") {
      regex += "[^/]";
    } else if ("\\^$.|+(){}[]".includes(ch)) {
      regex += "\\" + ch;
    } else {
      regex += ch;
    }
  }
  // Anchored patterns match from the root; unanchored patterns match a
  // path segment anywhere in the tree (the gitignore default).
  const prefix = anchored ? "^" : "(^|.*/)";
  return new RegExp(`${prefix}${regex}(/.*)?$`);
}

/**
 * Decide whether a path (relative to the folder root, forward-slashed)
 * should be hidden. Applies the built-in denylist first, then user
 * gitignore patterns.
 */
export function isIgnored(
  relativePath: string,
  patterns: Pattern[],
  isDirectory: boolean,
): boolean {
  const segments = relativePath.split("/");
  for (const seg of segments) {
    if (DEFAULT_DENYLIST.includes(seg)) return true;
  }

  let ignored = false;
  for (const p of patterns) {
    if (p.dirOnly && !isDirectory) continue;
    if (p.regex.test(relativePath)) {
      ignored = !p.negate;
    }
  }
  return ignored;
}

export type { Pattern };
